import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, effect, input, output, signal } from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { AgentStepItemComponent } from './step.item/step.item.component';
import type { StepType, StepStatus } from './step.item/step.item.component';
import { AgentToolResultSummaryComponent } from '../tool.result.summary/tool.result.summary.component';
import { AgentUtility } from '../../agent.utility';
import type { ToolResultSummary } from '../../agent.utility';
import type { AnyPart, ActivityBlockPart } from '../../agent.types';

/** A single rendered row inside the activity block — reasoning text, a tool/intent step, or response text. */
interface ActivityEntry {
  key: string;
  type: 'reasoning' | 'tool' | 'text';
  partIndex: number;
  part: AnyPart;
  label: string;
  meta: string;
  stepType: StepType;
  stepStatus: StepStatus;
  toolCallId: string;
  errorText: string;
  outputSummary: ToolResultSummary;
  expandable: boolean;
}

const WORKING_WORD_ROTATION_MS = 30_000;

@Component({
  selector: 'agent-activity-block',
  standalone: true,
  imports: [MarkdownComponent, AgentStepItemComponent, AgentToolResultSummaryComponent],
  templateUrl: './activity.block.component.html',
  styleUrls: ['./activity.block.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.live]': '$isLive()',
    '[class.collapsed]': '$isCollapsed()',
  },
})
export class AgentActivityBlockComponent {

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  public readonly $parts = input.required<ActivityBlockPart[]>({ alias: 'parts' });
  public readonly $isCollapsed = input<boolean>(true, { alias: 'isCollapsed' });
  public readonly $revealedTextParts = input<Record<string, string>>({}, { alias: 'revealedTextParts' });
  public readonly $messageId = input.required<string>({ alias: 'messageId' });
  public readonly $expandedTools = input<Set<string>>(new Set(), { alias: 'expandedTools' });

  // ─── Outputs ────────────────────────────────────────────────────────────────

  public readonly toggleCollapse = output<void>();
  public readonly toolExpandToggled = output<string>();

  // ─── Working word (live, rotating, shimmer + roll) ────────────────────────────

  /** The current playful working word, rotated while the block is live. */
  protected readonly $workingWord = signal<string>(AgentUtility.randomWorkingWord());

  public constructor() {

    // While live, rotate the playful word on a slow cadence. Each change re-keys the
    // template span so its roll-up animation replays. Auto-cleared when not live.
    effect((onCleanup) => {

      if (!this.$isLive()) return;
      this.$workingWord.set(AgentUtility.randomWorkingWord());
      const intervalId = setInterval(() => this.$workingWord.set(AgentUtility.randomWorkingWord()), WORKING_WORD_ROTATION_MS);
      onCleanup(() => clearInterval(intervalId));
    });
  }

  // ─── Computed ───────────────────────────────────────────────────────────────

  public readonly $isLive = computed(() => {

    const parts = this.$parts();
    if (parts.length === 0) return false;
    const last = parts[parts.length - 1];
    if (last.part.type === 'reasoning') {
      return !AgentUtility.partField(last.part, 'text', '');
    }
    if (AgentUtility.isToolPart(last.part)) {
      const state = AgentUtility.partField<string>(last.part, 'state', '');
      return state === 'input-streaming' || state === 'input-available' || state === 'approval-responded';
    }
    return false;
  });

  /** Summary label shown once the block is done (live state shows the rotating word instead). */
  public readonly $headerText = computed(() => AgentUtility.activityBlockSummaryLabel(this.$parts()));

  /**
   * Flattens parts into rendered rows. Reasoning/text pass through unchanged.
   * The hidden code-execution wrapper is expanded into one friendly intent step per
   * inner `toolCalls` entry (never a visible "Code_execution" row); all other tool
   * parts render as a normal step with an expandable, sanitized result summary.
   */
  public readonly $enrichedEntries = computed<ActivityEntry[]>(() => {

    const entries: ActivityEntry[] = [];

    for (const entry of this.$parts()) {
      const part = entry.part;
      const partIndex = entry.partIndex;

      if (part.type === 'reasoning' || part.type === 'text') {
        entries.push(AgentActivityBlockComponent.textEntry(part, partIndex));
      } else if (AgentUtility.isCodeExecutionPart(part)) {
        entries.push(...AgentActivityBlockComponent.intentEntries(part, partIndex));
      } else if (AgentUtility.isToolPart(part)) {
        entries.push(AgentActivityBlockComponent.toolEntry(part, partIndex));
      }
    }

    return entries;
  });

  // ─── Template Helpers ───────────────────────────────────────────────────────

  protected renderedTextContent(partIndex: number, part: AnyPart): string {

    const key = `${this.$messageId()}-${partIndex}`;
    return this.$revealedTextParts()[key] ?? AgentUtility.partField(part, 'text', '');
  }

  protected onHeaderClick(): void {

    this.toggleCollapse.emit();
  }

  protected onToolExpandClick(callId: string, event: MouseEvent): void {

    event.stopPropagation();
    this.toolExpandToggled.emit(callId);
  }

  // ─── Entry builders ──────────────────────────────────────────────────────────

  private static textEntry(part: AnyPart, partIndex: number): ActivityEntry {

    return AgentActivityBlockComponent.emptyEntry({
      key: `p${partIndex}`,
      type: part.type === 'reasoning' ? 'reasoning' : 'text',
      partIndex,
      part,
    });
  }

  /** Expands a code-execution part into friendly intent steps mined from its toolCalls. */
  private static intentEntries(part: AnyPart, partIndex: number): ActivityEntry[] {

    const partState = AgentUtility.partField<string>(part, 'state', '');
    // Drop blocked sub-calls (e.g. the per-code-execution tool-call budget guard). These
    // never actually ran — they are an internal signal telling the model to split work into
    // a new code_execution call — so they should not surface as a red error step to the user.
    const calls = AgentUtility.codeExecutionToolCalls(part).filter((call) => !AgentUtility.isToolCallBudgetError(call.error));

    // Whole-step failure (sandbox/python error): friendly message, no mechanics leaked.
    if (partState === 'output-error') {
      return [AgentActivityBlockComponent.emptyEntry({ key: `p${partIndex}-err`, type: 'tool', partIndex, part, label: 'Couldn’t complete that step', stepType: 'tool', stepStatus: 'error' })];
    }

    if (calls.length === 0) {
      // Still running (no toolCalls yet): show nothing — the live working word covers it.
      if (partState !== 'output-available') return [];
      // Finished but nothing surfaced: a single neutral step so the block is never empty.
      return [AgentActivityBlockComponent.emptyEntry({ key: `p${partIndex}-work`, type: 'tool', partIndex, part, label: 'Worked through your data', stepType: 'tool', stepStatus: 'complete' })];
    }

    return calls.map((call, i) => AgentActivityBlockComponent.emptyEntry({
      key: `p${partIndex}-c${i}`,
      type: 'tool',
      partIndex,
      part,
      label: AgentUtility.intentLabel(call.toolName, call.input),
      // Raw sub-call error strings are model-facing guidance ("try again in a moment"),
      // not user copy — the warning icon plus a neutral line is all the user needs; the
      // agent's own reply explains anything that actually mattered.
      meta: call.error ? 'This step didn’t go through' : AgentUtility.intentMeta(call),
      stepType: AgentActivityBlockComponent.resolveStepType(call.toolName),
      stepStatus: call.error ? 'error' : 'complete',
    }));
  }

  /** A normal (non-code-execution) tool step with an expandable, sanitized output summary. */
  private static toolEntry(part: AnyPart, partIndex: number): ActivityEntry {

    const rawName = AgentUtility.partField(part, 'toolName', '') || part.type.replace(/^tool-/, '');
    const state = AgentUtility.partField<string>(part, 'state', 'unknown');
    const output = AgentUtility.partField<unknown>(part, 'output', undefined);
    const outputSummary = AgentUtility.summarizeToolResult(output);

    return AgentActivityBlockComponent.emptyEntry({
      key: `p${partIndex}`,
      type: 'tool',
      partIndex,
      part,
      label: rawName ? AgentUtility.formatToolName(rawName, state) : 'Tool',
      stepType: AgentActivityBlockComponent.resolveStepType(rawName),
      stepStatus: AgentActivityBlockComponent.resolveStepStatus(state),
      toolCallId: AgentUtility.partField(part, 'toolCallId', ''),
      errorText: AgentUtility.partField(part, 'errorText', 'Tool execution failed'),
      outputSummary,
      // Skill tools (skill / skill_search / skill_read) show only their name + step in
      // the UI — never their output. That output is the skill's own instructions/
      // reference contents, which aren't useful to the user and shouldn't be leaked,
      // so keep these steps non-expandable while every other tool stays expandable.
      expandable: !AgentUtility.isSkillPart(part) && state === 'output-available' && (outputSummary.headline.length > 0 || outputSummary.rows.length > 0),
    });
  }

  /** Builds an entry with sensible defaults so each builder only sets what it needs. */
  private static emptyEntry(overrides: Partial<ActivityEntry> & Pick<ActivityEntry, 'key' | 'type' | 'partIndex' | 'part'>): ActivityEntry {

    return { label: '', meta: '', stepType: 'tool', stepStatus: 'complete', toolCallId: '', errorText: '', outputSummary: { headline: '', rows: [] }, expandable: false, ...overrides };
  }

  // ─── Private Static ────────────────────────────────────────────────────────

  private static resolveStepType(rawName: string): StepType {

    if (rawName === 'skill' || rawName === 'skill_search' || rawName === 'skill_read') return 'skill';
    const name = rawName.toLowerCase();
    if (name.includes('search') || name.includes('lookup') || name.includes('find') || name.includes('query') || name.includes('get')) return 'search';
    if (name.includes('create') || name.includes('add') || name.includes('update') || name.includes('delete') || name.includes('edit') || name.includes('write') || name.includes('set')) return 'edit';
    if (name.includes('file') || name.includes('download') || name.includes('upload') || name.includes('present')) return 'file';
    return 'tool';
  }

  private static resolveStepStatus(state: string): StepStatus {

    switch (state) {
      case 'output-available': return 'complete';
      case 'output-error': return 'error';
      case 'output-denied': return 'denied';
      case 'input-streaming':
      case 'input-available':
      case 'approval-responded': return 'current';
      default: return 'incomplete';
    }
  }
}
